import {
  type UploadResult,
  getDownloadURL,
  ref,
  uploadBytes
} from 'firebase/storage';
import {storage} from '../firebase';

import {
  Confidentiality,
  type Project,
  type Media,
  ProjectType,
  type ProjectDraft
} from '../../types/project';

import {
  TEMPLATE_PROJECT_SLIDES_IDS,
  TEMPLATE_DEVELOPMENT_PROJECT_SLIDE_ID,
  TEMPLATE_CONSULTING_PROJECT_SLIDE_ID,
  TEMPLATE_DIAMOND_SLIDE_ID,
  TEMPLATE_IMAGE_SLIDES_IDS,
  TEMPLATE_RELATED_PROJECTS_SLIDE_ID,
  CONFIDENTIAL_BADGE_IDS,
  DRIVE_FOLDER_NAME
} from '../../configs/slides';
import {youtubeVideoUrlRegex, driveVideoUrlRegex} from '../../constants/urls';
import {diamondTriangles} from '../../constants/diamond';
import environment from '../../constants/environment';
import {VIEW_PROJECT} from '../../constants/views';

import getContentMapping from './content-mapping';

interface MediaSlideData {
  type: Media['type'];
  orientation: 'portrait' | 'landscape';
  media: Array<Media>;
}

/**
 * Duplicate a given presentation file in Google Drive within a given parent folder and return the ID of the duplicate
 */
export function duplicateTemplate(
  templateId: string,
  parentId = 'root',
  fileName = `Project Gallery ${new Date().toLocaleString('de-DE')}`
): Promise<string> {
  return new Promise((resolve, reject) => {
    gapi.client.drive.files
      .copy({
        fileId: templateId,
        resource: {
          name: fileName,
          parents: [parentId]
        }
      })
      .then(driveResponse => {
        const presentationId = driveResponse.result.id || '';
        resolve(presentationId);
      }, reject)
      .catch(error => {
        reject(error);
      });
  });
}

interface templateProjectData {
  title: string;
  description_de: string;
  description_en?: string;
  teamAndLinks: string;
}

/**
 * Generate slides for multiple gallery projects
 */
export function createProjectSlides(
  presentationId: string,
  projects: Array<Project | ProjectDraft>
) {
  return new Promise(async (resolve, reject) => {
    let requests: Array<
      gapi.client.slides.Request | gapi.client.slides.Request[]
    > = [];

    requests = await Promise.all(
      projects.reverse().map(async (project, projectIndex) => {
        const slideIdsPerProject: Array<string> = [];
        const badgeIdsPerProject: Array<string> = [];
        const hasEnglishContent = Boolean(
          project.description_En && project.title_En
        );

        // Duplicate text slides with placeholders and add description slide twice if there is english title and description
        const projectRequests: Array<gapi.client.slides.Request> = [];

        let templateProjectSlides: templateProjectData = {
          ...TEMPLATE_PROJECT_SLIDES_IDS
        };

        if (!hasEnglishContent) {
          delete templateProjectSlides.description_en;
        }

        const entries = Object.entries(templateProjectSlides) as Array<
          [keyof typeof TEMPLATE_PROJECT_SLIDES_IDS, string]
        >;
        entries.map(([name, id]) => {
          const duplicateSlideId = `project_${projectIndex}_${name}`;
          slideIdsPerProject.push(duplicateSlideId);

          const duplicateBadgeId = `project_${projectIndex}_${name}_confidential`;
          if (CONFIDENTIAL_BADGE_IDS[name]) {
            badgeIdsPerProject.push(duplicateBadgeId);
          }

          projectRequests.push({
            duplicateObject: {
              objectId: id,
              objectIds: {
                [TEMPLATE_PROJECT_SLIDES_IDS[name]]: duplicateSlideId,
                [CONFIDENTIAL_BADGE_IDS[name]]: duplicateBadgeId
              }
            }
          });
        });

        // Duplicate development or consulting slide depending on the project type
        const detailsSlideId =
          project.type === ProjectType.DEVELOPMENT
            ? TEMPLATE_DEVELOPMENT_PROJECT_SLIDE_ID
            : TEMPLATE_CONSULTING_PROJECT_SLIDE_ID;

        const duplicateDetailsSlideId = `project_${projectIndex}_details`;
        slideIdsPerProject.push(duplicateDetailsSlideId);

        const duplicateDetailsBadgeId = `project_${projectIndex}_details_confidential`;
        badgeIdsPerProject.push(duplicateDetailsBadgeId);

        projectRequests.push({
          duplicateObject: {
            objectId: detailsSlideId,
            objectIds: {
              [detailsSlideId]: duplicateDetailsSlideId,
              [CONFIDENTIAL_BADGE_IDS[project.type]]: duplicateDetailsBadgeId
            }
          }
        });

        // Diamond slide
        const diamond = project.diamond;
        const hasDiamondValues =
          diamond && Object.values(diamond).some(value => value > 0);

        if (hasDiamondValues) {
          // Duplicate diamond template slide
          const duplicateDiamondSlideId = `project_${projectIndex}_diamond`;
          slideIdsPerProject.push(duplicateDiamondSlideId);

          const duplicateBadgeId = `project_${projectIndex}_diamond_confidential`;
          badgeIdsPerProject.push(duplicateBadgeId);

          projectRequests.push({
            duplicateObject: {
              objectId: TEMPLATE_DIAMOND_SLIDE_ID,
              objectIds: {
                [TEMPLATE_DIAMOND_SLIDE_ID]: duplicateDiamondSlideId,
                [CONFIDENTIAL_BADGE_IDS.diamond]: duplicateBadgeId
              }
            }
          });

          // Add diamond image to slide
          // Create diamond SVG
          const diamondSvg = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'svg'
          );
          diamondSvg.setAttribute('viewBox', '0 0 8 8');
          diamondTriangles.forEach(({getPath, color}) => {
            const diamondTrianglePath = document.createElementNS(
              'http://www.w3.org/2000/svg',
              'path'
            );
            diamondTrianglePath.setAttribute('d', getPath(diamond));
            diamondTrianglePath.setAttribute('fill', color);
            diamondSvg.appendChild(diamondTrianglePath);
          });

          // Get diamond image Blob
          const diamondSvgString = new XMLSerializer().serializeToString(
            diamondSvg
          );
          const base64 = window.btoa(diamondSvgString);
          const diamondSvgDataUrl = `data:image/svg+xml;base64,${base64}`;
          const diamondBlob: Blob | null = await new Promise(resolve => {
            const image = new Image();
            image.src = diamondSvgDataUrl;
            image.onload = () => {
              const canvas = document.createElement('canvas');
              const size = 850;
              canvas.width = size;
              canvas.height = size;
              const context = canvas.getContext('2d');
              context?.drawImage(image, 0, 0, size, size, 0, 0, size, size);
              canvas.toBlob(resolve, 'image/png');
            };
          });

          // Upload diamond image to Cloud Storage
          if (diamondBlob) {
            const storageRef = ref(
              storage,
              `${environment}/${project.id}/diamond.png`
            );
            const uploadResult: UploadResult = await uploadBytes(
              storageRef,
              diamondBlob
            );
            const imageUrl = await getDownloadURL(uploadResult.ref);

            // Add uploaded image to slide
            projectRequests.push({
              replaceAllShapesWithImage: {
                imageUrl,
                imageReplaceMethod: 'CENTER_INSIDE',
                pageObjectIds: [duplicateDiamondSlideId],
                containsText: {
                  text: '{{ diamond }}',
                  matchCase: true
                }
              }
            });
          }
        }

        // Organize media slides, combine images in one slide if possible
        const mediaSlides = project.media?.reduce(
          (slides: Array<MediaSlideData>, mediaObject: Media) => {
            const previousMediaSlide = slides.length
              ? slides[slides.length - 1]
              : null;

            const orientation: MediaSlideData['orientation'] =
              mediaObject.type === 'image' &&
              mediaObject.height > mediaObject.width
                ? 'portrait'
                : 'landscape';

            const mediaSlide = {
              type: mediaObject.type,
              orientation,
              media: [mediaObject]
            };

            // Combine consecutive portrait images in one slide (max 3 images per slide)
            if (
              orientation === 'portrait' &&
              previousMediaSlide?.orientation === 'portrait' &&
              previousMediaSlide.media.length < 3
            ) {
              mediaSlide.media = [...previousMediaSlide.media, mediaObject];
              slides.pop();
            }

            return [...slides, mediaSlide];
          },
          []
        );

        // Duplicate as many image template slides as needed and replace placeholders
        if (mediaSlides) {
          for (const [mediaSlideIndex, mediaSlide] of mediaSlides.entries()) {
            let mediaSlideCounter = mediaSlideIndex + 1;

            if (mediaSlide.type === 'image') {
              const duplicateImageSlideId = `project_${projectIndex}_image_${mediaSlideCounter}`;
              slideIdsPerProject.push(duplicateImageSlideId);

              const duplicateBadgeId = `project_${projectIndex}_image_${mediaSlideCounter}_confidential`;
              badgeIdsPerProject.push(duplicateBadgeId);

              projectRequests.push({
                duplicateObject: {
                  objectId: TEMPLATE_IMAGE_SLIDES_IDS[mediaSlide.orientation],
                  objectIds: {
                    [TEMPLATE_IMAGE_SLIDES_IDS[mediaSlide.orientation]]:
                      duplicateImageSlideId,
                    [CONFIDENTIAL_BADGE_IDS.image[mediaSlide.orientation]]:
                      duplicateBadgeId
                  }
                }
              });

              mediaSlide.media.forEach((media, mediaIndex) => {
                projectRequests.push({
                  replaceAllShapesWithImage: {
                    imageUrl: media.url,
                    imageReplaceMethod: 'CENTER_INSIDE',
                    pageObjectIds: [duplicateImageSlideId],
                    containsText: {
                      text: `{{ image_${mediaIndex} }}`,
                      matchCase: true
                    }
                  }
                });
              });
            } else if (mediaSlide.type === 'video') {
              const duplicateVideoSlideId = `project_${projectIndex}_video_${mediaSlideCounter}`;

              const duplicateBadgeId = `project_${projectIndex}_image_${mediaSlideCounter}_confidential`;

              let source, videoId;

              const matchYoutube =
                mediaSlide.media[0].url.match(youtubeVideoUrlRegex);
              if (matchYoutube) {
                videoId = matchYoutube?.groups?.videoId;
                source = 'YOUTUBE';
              }
              const matchDrive =
                mediaSlide.media[0].url.match(driveVideoUrlRegex);
              if (matchDrive) {
                videoId = matchDrive?.groups?.videoId;
                source = 'DRIVE';
              }

              if (videoId) {
                const addVideoSlideAndRequest = (
                  videoId: string,
                  source?: string
                ) => {
                  slideIdsPerProject.push(duplicateVideoSlideId);

                  badgeIdsPerProject.push(duplicateBadgeId);

                  projectRequests.push(
                    {
                      duplicateObject: {
                        objectId:
                          TEMPLATE_IMAGE_SLIDES_IDS[mediaSlide.orientation],
                        objectIds: {
                          [TEMPLATE_IMAGE_SLIDES_IDS[mediaSlide.orientation]]:
                            duplicateVideoSlideId,
                          [CONFIDENTIAL_BADGE_IDS.image[
                            mediaSlide.orientation
                          ]]: duplicateBadgeId
                        }
                      }
                    },
                    {
                      createVideo: {
                        source,
                        id: videoId,
                        elementProperties: {
                          pageObjectId: duplicateVideoSlideId,
                          size: {
                            width: {
                              magnitude: 9144000,
                              unit: 'EMU'
                            },
                            height: {
                              magnitude: 5143500,
                              unit: 'EMU'
                            }
                          }
                        }
                      }
                    }
                  );
                };

                if (matchDrive) {
                  try {
                    await gapi.client.drive.files.get({
                      fileId: videoId
                    });
                    addVideoSlideAndRequest(videoId, source);
                  } catch {
                    // Do not add the file to slides if it is not accessible
                  }
                } else {
                  addVideoSlideAndRequest(videoId, source);
                }
              }
            }
          }
        }

        // Add related projects slide if available
        if (project.relatedProjects?.length) {
          const duplicateRelatedProjectsSlideId = `project_${projectIndex}_related_projects`;
          slideIdsPerProject.push(duplicateRelatedProjectsSlideId);

          const duplicateBadgeId = `project_${projectIndex}_related_projects_confidential`;
          badgeIdsPerProject.push(duplicateBadgeId);

          projectRequests.push({
            duplicateObject: {
              objectId: TEMPLATE_RELATED_PROJECTS_SLIDE_ID,
              objectIds: {
                [TEMPLATE_RELATED_PROJECTS_SLIDE_ID]:
                  duplicateRelatedProjectsSlideId,
                [CONFIDENTIAL_BADGE_IDS.relatedProjects]: duplicateBadgeId
              }
            }
          });
        }

        // Update text placeholders in description slide for english content
        if (hasEnglishContent) {
          [
            {
              presentationKey: '{{ title_small }}',
              projectProperty: project.title_En
            },
            {
              presentationKey: '{{ description }}',
              projectProperty: project.description_En
            }
          ].forEach(mapping => {
            projectRequests.push({
              replaceAllText: {
                containsText: {
                  text: mapping.presentationKey,
                  matchCase: true
                },
                replaceText: mapping.projectProperty,
                pageObjectIds: [`project_${projectIndex}_description_en`]
              }
            });
          });
        }

        // Update text placeholders in duplicated slides
        getContentMapping(project).forEach(mapping => {
          projectRequests.push({
            replaceAllText: {
              containsText: {
                text: mapping.presentationKey,
                matchCase: true
              },
              replaceText: mapping.projectProperty,
              pageObjectIds: slideIdsPerProject
            }
          });
        });

        // Remove confidential badge from non-confidential project slides
        if (project.confidentiality === Confidentiality.NOT_CONFIDENTIAL) {
          badgeIdsPerProject.map(objectId => {
            projectRequests.push({
              deleteObject: {objectId}
            });
          });
        }

        // Update speaker notes with project URL
        const projectUrlTemplate = `${window.location.origin}/#${VIEW_PROJECT}`;
        projectRequests.push({
          replaceAllText: {
            containsText: {
              text: '{{ project_url }}',
              matchCase: true
            },
            replaceText: `Project URL: ${projectUrlTemplate.replace(
              ':id',
              project.id
            )}`,
            pageObjectIds: [
              ...Object.keys(templateProjectSlides).map(
                name => `project_${projectIndex}_${name}:notes`
              ),
              `project_${projectIndex}_details:notes`,
              ...(hasDiamondValues
                ? [`project_${projectIndex}_diamond:notes`]
                : [])
            ]
          }
        });

        // Bring slides in the right order (duplicates always appear after the original slide, e.g "A, Copy1(A), Copy2(A), B, Copy1(B), Copy2(B)")
        slideIdsPerProject.reverse().forEach(slideId => {
          projectRequests.push({
            updateSlidesPosition: {
              slideObjectIds: [slideId],
              insertionIndex: 2 // Keep first two cover slides at the beginning of the presentation
            }
          });
        });

        return projectRequests;
      })
    );
    // Delete template project slides
    // Remove duplicate slide ids
    new Set([
      ...Object.values(TEMPLATE_PROJECT_SLIDES_IDS),
      TEMPLATE_DEVELOPMENT_PROJECT_SLIDE_ID,
      TEMPLATE_CONSULTING_PROJECT_SLIDE_ID,
      TEMPLATE_DIAMOND_SLIDE_ID,
      ...Object.values(TEMPLATE_IMAGE_SLIDES_IDS),
      TEMPLATE_RELATED_PROJECTS_SLIDE_ID
    ]).forEach(id => {
      requests.push({
        deleteObject: {
          objectId: id
        }
      });
    });

    batchUpdate(presentationId, requests.flat())
      .then(resolve, reject)
      .catch(error => {
        reject(error);
      });
  });
}

/**
 * Return the URL to a given presentation ID
 */
export function getUrl(presentationId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    gapi.client.drive.files
      .get({
        fileId: presentationId,
        fields: 'webViewLink'
      })
      .then(response => {
        const presentationUrl = response.result.webViewLink || '';
        resolve(presentationUrl);
      }, reject)
      .catch(error => {
        reject(error);
      });
  });
}

/**
 * Promise-based wrapper for Google API batchUpdate call, that automatically checks the response for validity
 */
function batchUpdate(
  presentationId: string,
  requests: Array<gapi.client.slides.Request>
): Promise<string> {
  return new Promise((resolve, reject) => {
    gapi.client.slides.presentations
      // @ts-ignore: 'requests' does not exist in type
      .batchUpdate({presentationId, requests})
      .then(({result: batchUpdateResponse}) => {
        if (batchUpdateResponse.replies?.length === requests.length) {
          resolve(presentationId);
        } else {
          reject('Some updates could not be executed.');
        }
      }, reject)
      .catch(error => {
        reject(error);
      });
  });
}

/**
 * Find or create a new folder with the name defined in a config
 * file and return its id.
 */
export function findOrCreateFolder(): Promise<string> {
  return new Promise((resolve, reject) => {
    gapi.client.drive.files
      .list({
        q: `mimeType = 'application/vnd.google-apps.folder' and name = '${DRIVE_FOLDER_NAME}'`,
        spaces: 'drive'
      })
      .then(({result}) => {
        if (result?.files?.length && result.files[0].id) {
          // Folder has been found
          resolve(result.files[0].id);
        } else {
          gapi.client.drive.files
            .create({
              resource: {
                name: DRIVE_FOLDER_NAME,
                mimeType: 'application/vnd.google-apps.folder'
              },
              fields: 'id'
            })
            .then(response => {
              const newFolderId = response?.result?.id;
              if (newFolderId) {
                // Folder created successfully
                resolve(newFolderId);
              }
              reject('Folder creation failed.');
            })
            .catch(error => {
              reject(error);
            });
        }
      })
      .catch(error => {
        reject(error);
      });
  });
}
